import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API } from '@/App';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { DownloadSimple, FilePdf, Spinner } from '@phosphor-icons/react';

const fmtEUR = (v) => new Intl.NumberFormat('fr-FR', { style: 'currency', currency: 'EUR' }).format(Number(v) || 0);

function currentMonthValue() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export default function MonthlyRecapDialog({ open, onOpenChange }) {
  const [month, setMonth] = useState(currentMonthValue());
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [selected, setSelected] = useState(new Set());
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    (async () => {
      setLoading(true);
      setData(null);
      setSelected(new Set());
      try {
        const res = await axios.get(`${API}/admin/monthly-orders?month=${month}`);
        if (!cancelled) setData(res.data);
      } catch (err) {
        if (!cancelled) toast.error(err.response?.data?.detail || 'Erreur de chargement');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [open, month]);

  const toggle = (cid) => {
    const next = new Set(selected);
    if (next.has(cid)) next.delete(cid); else next.add(cid);
    setSelected(next);
  };

  const toggleAll = () => {
    if (!data?.clients?.length) return;
    if (selected.size === data.clients.length) setSelected(new Set());
    else setSelected(new Set(data.clients.map(c => c.client_id)));
  };

  const totals = useMemo(() => {
    if (!data?.clients) return { orders: 0, ht: 0, catalog: 0 };
    const rows = data.clients.filter(c => selected.has(c.client_id));
    return rows.reduce((acc, r) => ({
      orders: acc.orders + (r.orders_count || 0),
      ht: acc.ht + (r.total_ht || 0),
      catalog: acc.catalog + (r.total_catalog_ht || 0),
    }), { orders: 0, ht: 0, catalog: 0 });
  }, [data, selected]);

  const handleGenerate = async () => {
    if (selected.size === 0) {
      toast.error('Sélectionnez au moins un client');
      return;
    }
    setGenerating(true);
    try {
      const res = await axios.post(
        `${API}/admin/monthly-orders/pdf`,
        { month, client_ids: Array.from(selected) },
        { responseType: 'blob' }
      );
      const url = window.URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const a = document.createElement('a');
      a.href = url;
      a.download = `recap_${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`PDF généré : ${selected.size} client(s)`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Erreur de génération PDF');
    } finally {
      setGenerating(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl bg-white" data-testid="monthly-recap-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FilePdf size={20} weight="duotone" />
            Export récapitulatif de commandes
          </DialogTitle>
          <DialogDescription>
            Sélectionnez un mois puis cochez les clients pour générer un PDF récapitulatif détaillé.
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-end gap-3 mb-2">
          <div className="flex-1 max-w-xs">
            <label className="text-xs uppercase tracking-wider text-zinc-500 mb-1 block">Mois</label>
            <Input
              type="month"
              value={month}
              onChange={(e) => setMonth(e.target.value)}
              className="rounded-sm"
              data-testid="recap-month-input"
            />
          </div>
          <div className="text-sm text-zinc-600">
            <span className="font-semibold text-zinc-900">{data?.month_label || '—'}</span>
            {data?.clients?.length > 0 && <span className="ml-2 text-xs">· {data.clients.length} client(s)</span>}
          </div>
        </div>

        <div className="border border-zinc-200 rounded-sm overflow-hidden">
          <Table>
            <TableHeader className="bg-zinc-50">
              <TableRow>
                <TableHead className="w-10">
                  <Checkbox
                    checked={data?.clients?.length > 0 && selected.size === data.clients.length}
                    onCheckedChange={toggleAll}
                    data-testid="recap-select-all"
                  />
                </TableHead>
                <TableHead>Client</TableHead>
                <TableHead className="text-right w-24">Commandes</TableHead>
                <TableHead className="text-right w-32">Prix catalogue</TableHead>
                <TableHead className="text-right w-32">Prix remisé</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading && (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-zinc-500">
                  <Spinner size={20} className="inline animate-spin mr-2" />Chargement…
                </TableCell></TableRow>
              )}
              {!loading && (!data?.clients || data.clients.length === 0) && (
                <TableRow><TableCell colSpan={5} className="text-center py-6 text-zinc-500">
                  Aucune commande pour ce mois
                </TableCell></TableRow>
              )}
              {!loading && data?.clients?.map((c) => (
                <TableRow
                  key={c.client_id}
                  className={`cursor-pointer hover:bg-zinc-50 ${selected.has(c.client_id) ? 'bg-zinc-100' : ''}`}
                  onClick={() => toggle(c.client_id)}
                  data-testid={`recap-client-row-${c.client_id}`}
                >
                  <TableCell onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                      checked={selected.has(c.client_id)}
                      onCheckedChange={() => toggle(c.client_id)}
                      data-testid={`recap-client-checkbox-${c.client_id}`}
                    />
                  </TableCell>
                  <TableCell>
                    <p className="font-medium text-sm">{c.client_name}</p>
                    {c.company_name && <p className="text-xs text-zinc-500">{c.company_name}</p>}
                  </TableCell>
                  <TableCell className="text-right text-sm font-medium">{c.orders_count}</TableCell>
                  <TableCell className="text-right text-sm text-zinc-500">{fmtEUR(c.total_catalog_ht)}</TableCell>
                  <TableCell className="text-right text-sm font-semibold">{fmtEUR(c.total_ht)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>

        {selected.size > 0 && (
          <div className="bg-zinc-900 text-white px-4 py-3 rounded-sm flex items-center justify-between" data-testid="recap-selection-summary">
            <div className="text-xs">
              <span className="font-bold">{selected.size}</span> client(s) sélectionné(s) · <span className="font-bold">{totals.orders}</span> commande(s)
              <span className="mx-3 text-zinc-400">|</span>
              Prix catalogue : <span className="font-bold">{fmtEUR(totals.catalog)}</span>
              <span className="mx-3 text-zinc-400">·</span>
              Prix remisé : <span className="font-bold text-emerald-300">{fmtEUR(totals.ht)}</span>
            </div>
          </div>
        )}

        <div className="flex justify-end gap-2 mt-2">
          <Button variant="outline" onClick={() => onOpenChange(false)} className="rounded-sm" data-testid="recap-close">
            Fermer
          </Button>
          <Button
            onClick={handleGenerate}
            disabled={selected.size === 0 || generating}
            className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white"
            data-testid="recap-generate-pdf"
          >
            {generating ? <><Spinner size={16} className="mr-2 animate-spin" />Génération…</> :
              <><DownloadSimple size={16} className="mr-2" />Générer PDF</>}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
